/**
 * The step rail both Network Collectors wizards wear.
 *
 * Extracted rather than written twice: Add Collector and Add Log Source are two
 * different two-step flows, but "which step am I on" is one control, and a
 * second copy of it is the drift `log-source-table-frame` and `wizard-frame`
 * exist to stop. **Eager**, because both wizards' skeletons draw it for real —
 * the rail knows the step from the route, not from a server (P-05 §1).
 */
import { cn } from '@/utils/ui/styles'

type WizardStepRailProps = {
	/** The flow's steps, in order. Their position is their number. */
	steps: readonly string[]
	/** Which one is current. **One-based**, as the rail numbers it. */
	activeStep: number
}

/**
 * An ordered list rather than a row of divs, because that is what it is: the
 * steps have an order and a current position, and `aria-current="step"` is the
 * only part of "step 1 is the blue one" that survives without colour.
 */
export const WizardStepRail = ({ steps, activeStep }: WizardStepRailProps) => {
	return (
		<ol className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
			{steps.map((label, index) => {
				const step = index + 1
				const isActive = step === activeStep

				return (
					<li
						key={label}
						aria-current={isActive ? 'step' : undefined}
						className="flex items-center gap-2"
					>
						{index > 0 ? (
							<span aria-hidden className="text-muted-foreground">
								·
							</span>
						) : null}

						<span
							aria-hidden
							className={cn(
								'flex size-6 items-center justify-center rounded-full text-xs font-medium',
								isActive
									? 'bg-primary text-primary-foreground'
									: 'bg-muted text-muted-foreground',
							)}
						>
							{step}
						</span>

						<span className={isActive ? undefined : 'text-muted-foreground'}>
							{label}
						</span>
					</li>
				)
			})}
		</ol>
	)
}
